import React, {
    useContext, useState, useCallback, useMemo, useEffect
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { InlineLoader, Tooltip, Card } from '@jutro/components';
import { useTranslator } from '@jutro/locale';
import { BreakpointTrackerContext } from '@jutro/layout';
import { pageTemplateProps } from '@xengage/gw-portals-wizard-react';
import { screenReaderAnnounce } from 'premier-capability-hooks';

import { Button } from '@jutro/legacy/components';
import styles from './DigitalWizardPageTemplate.module.scss';
import WizardSidebar from '../components/WizardSidebar';
import MobileWizardSteps from '../components/MobileWizardSteps/MobileWizardSteps';
import messages from '../components/WizardSidebar.messages';


function renderLoader(isLoading) {
    return isLoading ? <InlineLoader loading={isLoading} className="gw-inline-loader" /> : null;
}

function DigitalWizardPageTemplate(props) {
    const translator = useTranslator();
    const breakpoint = useContext(BreakpointTrackerContext);
    const isPhone = breakpoint === 'phoneWide' || breakpoint === 'phone';
    const [showSidebar, setShowSidebar] = useState(false);
    const [showSaveLoader, setShowSaveLoader] = useState(false);
    const {
        children: content,
        onNext,
        isLoadingNext,
        disableNext,
        showNext,
        disableSave,
        isLoadingSave,
        onSave,
        nextLabel,
        nextButtonTooltip,
        onPrevious,
        isLoadingPrevious,
        disablePrevious,
        showPrevious,
        previousLabel,
        onCancel,
        isLoadingCancel,
        disableCancel,
        showCancel,
        cancelLabel,
        renderContextComponent,
        renderWidgets,
        wizardData,
        wizardSnapshot,
        showContextComponent,
        showWizardHeader,
        customMessageModal
    } = props;

    useEffect(() => {
        switch (breakpoint) {
            case 'phoneWide':
            case 'phone':
                setShowSidebar(false);
                break;
            default:
                setShowSidebar(true);
                break;
        }
    }, [breakpoint]);

    const toggleSideBar = useCallback(() => {
        setShowSidebar(!showSidebar);
    }, [showSidebar]);

    const contextComponent = useCallback(() => <div className={styles.wizardContext}>{renderContextComponent(wizardSnapshot)}</div>, [renderContextComponent, wizardSnapshot]);

    const shouldShowButtons = useMemo(() => !(
        showNext === false
        && showPrevious === false
        && showCancel === false
    ), [showCancel, showNext, showPrevious]);

    const isPerformingTransiton = useMemo(() => (
        isLoadingCancel || isLoadingPrevious || isLoadingNext || isLoadingSave || showSaveLoader
    ), [isLoadingCancel, isLoadingNext, isLoadingPrevious, isLoadingSave, showSaveLoader]);

    const wizardClassName = classNames(styles.wizardMain, {
        [styles.wizardMainShowSidebar]: showSidebar && isPhone
    });

    const nextButton = useMemo(() => (
        <Button
            id="gw-wizard-Next"
            className={styles.nextbutton}
            tabindex={disableNext ? -1 : 0}
            disabled={disableNext || isPerformingTransiton}
            onClick={onNext}
        >
            {translator(nextLabel)}
            {renderLoader(isLoadingNext)}
        </Button>
    ), [disableNext, isLoadingNext, isPerformingTransiton, nextLabel, onNext, translator]);

    const nextButtonWithTooltip = useMemo(() => (
        <Tooltip placement="bottom" content={nextButtonTooltip}>
            <span>{nextButton}</span>
        </Tooltip>
    ), [nextButton, nextButtonTooltip]);

    const renderNextButton = nextButtonTooltip ? nextButtonWithTooltip : nextButton;
    const buttonContainerClassName = classNames(styles.navTransitionButtons, {
        [styles.navTransitionButtonOnlyNext]: !showPrevious && isPhone
    });

    const saveButtonClick = useCallback(async () => {
        try{
            setShowSaveLoader(true);
            await onSave()
         } catch (error) { /* empty */ } finally {
            setShowSaveLoader(false);
        }

        screenReaderAnnounce('Changes saved successfully');   
    }, [onSave]);

    return (
        <div className={styles.digitalWizard}>
            {showContextComponent && contextComponent()}
            <div className={wizardClassName}>
                {showWizardHeader && contextComponent()}
                {!isPhone && showSidebar ? (
                    <div className={classNames(styles.wizardNavigation, 'hiddenPrintElement')}>
                        <WizardSidebar customMessageModal={customMessageModal} />
                        <div className={styles.wizardWidgets}>
                            {renderWidgets(wizardData)}
                        </div>
                    </div>
                ) : null}
                <div className={styles.wizardContent}>
                    {isPhone ? <MobileWizardSteps toggleSideBar={toggleSideBar} /> : null}
                    <Card
                        id="digitalWizardContent"
                        className={styles.wizardContentContainer}
                        fullWidth
                        isPanel>
                        {content}
                        <div className={classNames({ [styles.navButtons]: shouldShowButtons }, 'hiddenPrintElement')}>
                            {showCancel && (
                                <Button
                                    id="gw-wizard-cancel"
                                    disabled={disableCancel || isPerformingTransiton}
                                    type="text"
                                    onClick={onCancel}
                                >
                                    {translator(cancelLabel)}
                                    {renderLoader(isLoadingCancel)}
                                </Button>
                            )}
                            <div className={buttonContainerClassName}>
                                {showPrevious && (
                                    <Button
                                        id="gw-wizard-previous"
                                        disabled={disablePrevious || isPerformingTransiton}
                                        type="outlined"
                                        onClick={onPrevious}
                                    >
                                        {translator(previousLabel)}
                                        {renderLoader(isLoadingPrevious)}
                                    </Button>
                                )}
                                {showNext && renderNextButton}
                                {showNext && (
                                    <Button
                                        id="gw-wizard-save"
                                        className={styles.nextbutton}
                                        disabled={disableSave || isPerformingTransiton}
                                        tabindex={disableSave ? -1 : 0}
                                        type="outlined"
                                        onClick={saveButtonClick}
                                    >
                                        {translator(messages.save)}
                                        {renderLoader(showSaveLoader)}
                                    </Button>
                                )}
                            </div>
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );
}

DigitalWizardPageTemplate.propTypes = {
    ...pageTemplateProps,
    /** a function that accepts wizardData to render a React component */
    renderContextComponent: PropTypes.func,
    /** a function that accepts wizardData to render a React component */
    renderWidgets: PropTypes.func,
    showContextComponent: PropTypes.bool,
    showWizardHeader: PropTypes.bool,
    nextButtonTooltip: PropTypes.string
};

DigitalWizardPageTemplate.defaultProps = {
    renderContextComponent: () => null,
    renderWidgets: () => null,
    showContextComponent: true,
    showWizardHeader: false,
    nextButtonTooltip: ''
};

export default DigitalWizardPageTemplate;
